import type { FanoutSlice } from '~/api/hooks/useTimeframeFanout'
import { makeColorForKey } from './dimValueColors'
import { collectDimValueKeys, isGrouped } from './toRankedDimRows'

/** Shared coordination for the metric|driver bar pair (live tile AND pin replay):
 *  ONE color map over the dim's FULL catalog value list (value-keyed, catalog
 *  order — spec D3), with both arms' rendered keys as the overflow set, plus the
 *  small-multiples layout flag. Pass `driverSlices = null` when there is no
 *  driver arm; pass `catalogValues = []` when no catalog is available (pin
 *  replay without controls) — coloring then degrades to deterministic
 *  overflow-order over the rendered keys. Pure — safe after hooks/early returns. */
export function resolveArmColoring(
  metricSlices: FanoutSlice[],
  driverSlices: FanoutSlice[] | null,
  timeframeCount: number,
  catalogValues: string[],
  palette: string[],
): { colorForKey: (key: string) => string; smallMultiples: boolean } {
  const renderedKeys = [
    ...collectDimValueKeys(metricSlices),
    ...(driverSlices ? collectDimValueKeys(driverSlices) : []),
  ]
  const rawColorForKey = makeColorForKey(catalogValues, renderedKeys, palette)
  // unreachable: extraKeys covers every rendered key (overflow slots assigned above)
  const colorForKey = (key: string) => rawColorForKey(key) ?? palette[0]!
  const smallMultiples =
    timeframeCount > 1 &&
    (isGrouped(metricSlices) || (driverSlices ? isGrouped(driverSlices) : false))
  return { colorForKey, smallMultiples }
}
